import { isEmptyMetricValue } from 'partner360/features/financialCockpit/ui/cockpitRevenueAndSob/lib/metricPresentation'

import { IRevenueAndSobHierarchyRow } from '../../../lib'

/** The measures the table has a column for. `expectedExit`, `prevFYearExpectedExit` and `actuals`
 *  are mutually exclusive by construction — `buildFigures` nulls whichever the table leaves out —
 *  so all three can be counted without knowing `isPeriodOpen` or `isPrevFYearClosing`.
 *  `marketGrowth` and `sob.quota` are computed but never rendered, so they must not keep a row
 *  alive. */
const COLUMN_MEASURES = [
  'baseline',
  'plan',
  'expectedExit',
  'prevFYearExpectedExit',
  'actuals',
] as const

/** Whether this node has a figure behind any column currently on screen. `quota` is the one column
 *  whose visibility is a cockpit setting rather than a property of the data. */
const hasOwnValue = (row: IRevenueAndSobHierarchyRow, showQuota: boolean) =>
  COLUMN_MEASURES.some(
    (measure) => !isEmptyMetricValue(row.sales[measure]) || !isEmptyMetricValue(row.sob[measure])
  ) ||
  (showQuota && !isEmptyMetricValue(row.sales.quota))

/** Drops the nodes that would render as a full row of `--`, keeping every ancestor of a node that
 *  survives. Ancestors stay even when their own figures are empty: a node's figures come from its
 *  own backend rows rather than from summing its children (see `buildFigures`). Walking up from each
 *  populated node — rather than testing subtrees top-down — is what guarantees no row is ever left
 *  without its parent, which `useHierarchyExpansion`'s visibility walk would read as "hide this
 *  whole subtree". */
export const getPopulatedHierarchyRows = (
  rows: IRevenueAndSobHierarchyRow[],
  showQuota: boolean
): IRevenueAndSobHierarchyRow[] => {
  const rowsById = new Map(rows.map((row) => [row.rowId, row]))
  const populatedRowIds = new Set<string>()

  rows.forEach((row) => {
    if (!hasOwnValue(row, showQuota)) {
      return
    }

    let node: IRevenueAndSobHierarchyRow | undefined = row

    /** Stopping at the first already-marked ancestor keeps this near-linear. */
    while (node && !populatedRowIds.has(node.rowId)) {
      populatedRowIds.add(node.rowId)
      node = node.parentRowId ? rowsById.get(node.parentRowId) : undefined
    }
  })

  return rows.filter((row) => populatedRowIds.has(row.rowId))
}
